/**
 * CRM Provisioning Page (founder-only)
 *
 * Lets the founder provision ANY tenant's MyCRM (Loan Market / LMG) API
 * credentials from the admin portal, without logging into the tenant's client
 * app and without impersonation. Calls the `adminConnectTenantCrm` callable,
 * which forwards to the client-functions `connectMyCrmCredentials` via the
 * synthetic __platform_harness__ founder-bypass.
 *
 * The tenant must already hold the CRM integration entitlement (pro tier) —
 * otherwise provisioning fails with a clear failed-precondition message
 * surfaced below.
 *
 * Onboarding ergonomics (2026-07-21):
 *   - Adviser IDs are OPTIONAL: leave blank and connect auto-discovers the
 *     tenant's advisers from MyCRM and shows them for confirmation.
 *   - After connecting, the founder can trigger the tenant's first sync in
 *     place via `adminTriggerTenantCrmSync` and see the outcome.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { collection, getDocs, query, limit } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebaseConfig';
import {
  Plug,
  CheckCircle2,
  AlertTriangle,
  Loader2,
  RefreshCw,
  Users,
  ListChecks,
  Save,
  Search,
  X,
  Check,
  Trash2,
} from 'lucide-react';

interface CompanyOption {
  id: string;
  companyName?: string;
}

interface DiscoveredAdviser {
  id: string;
  email?: string;
  name: string;
}

interface ConnectResult {
  success: boolean;
  connectionId: string;
  message: string;
  discoveredAdvisers?: DiscoveredAdviser[];
  adviserContactIds?: string[];
  warning?: string;
  reusedExisting?: boolean;
}

interface SyncOutcome {
  connectionId: string;
  provider: string;
  status: 'success' | 'partial' | 'failed';
  advisersSynced: number;
  dealsSynced: number;
  alertsCreated: number;
  durationMs: number;
  errors: string[];
}

interface SyncResult {
  success: boolean;
  outcomes: SyncOutcome[];
}

// ─── CRM mappings (white-glove) ──────────────────────────────────────────────
// Mirrors the tenant-side useCrmMappings shapes exactly. Seeded via the
// founder-only `adminCrmMappingAction`, which forwards verbatim to the tenant
// `crmMappingAction` (adviser classification + adviser-to-broker link + lender
// name aliases). These are what the Trail Leakage detector reads to attribute
// deals, so a mis-seed mutes/false-fires alerts (hence the confirmation copy).

const ADVISER_ROLE_OPTIONS = [
  'broker',
  'operations',
  'marketing',
  'management',
  'general',
  'inactive',
] as const;
type AdviserRole = (typeof ADVISER_ROLE_OPTIONS)[number];

interface UnmappedLender {
  lender_string: string;
  occurrences?: number;
  loan_count?: number;
}
interface AdviserRow {
  adviser_id: string;
  email: string | null;
  adviser_name: string | null;
  valutten_broker_ids: string[];
  adviser_role: AdviserRole | null;
  deal_count: number;
}
interface InstitutionRow {
  institution_key: string;
  display_name: string;
  category: string;
}
interface BrokerCandidate {
  broker_display: string;
  broker_id: string;
  loans: number;
}
// A lender string in this tenant's data that already resolves through the
// GLOBAL lender_aliases registry (shared by every tenant).
interface MappedLender {
  source: 'mycrm' | 'raw_commissions';
  lender_string: string;
  seen: number;
  institution_key: string;
  institution_name: string | null;
  confidence: string | null;
  verified_at: string | null;
}
interface MappingsData {
  unmappedLenders: { mycrm: UnmappedLender[]; raw_commissions: UnmappedLender[] };
  advisers: AdviserRow[];
  institutions: InstitutionRow[];
  brokerCandidates: BrokerCandidate[];
  mappedLenders: MappedLender[];
}

// Case-insensitive substring match, anywhere in the haystack.
function matchesQuery(haystack: string, q: string): boolean {
  return haystack.toLowerCase().includes(q.trim().toLowerCase());
}

/**
 * Dependency-free single-select combobox for the canonical institution list.
 * Replaces the four alphabetical <optgroup> sections (unusable without search):
 * a text input filters the whole list by case-insensitive substring (match
 * anywhere), the filtered options render below grouped by category (the group
 * labels the four sections carried), click or Enter selects, and the current
 * selection is shown in the input. Escape closes. The save flow is unchanged:
 * onChange still yields an institution_key string.
 */
const InstitutionCombobox: React.FC<{
  institutions: InstitutionRow[];
  value: string;
  onChange: (institutionKey: string) => void;
  labelId: string;
  disabled?: boolean;
}> = ({ institutions, value, onChange, labelId, disabled }) => {
  const [open, setOpen] = useState(false);
  const [queryText, setQueryText] = useState('');
  const [highlight, setHighlight] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  const selected = institutions.find((i) => i.institution_key === value) || null;

  // When closed, the input shows the current selection; when open, it shows the
  // live filter text so the founder can type over it.
  const inputValue = open ? queryText : selected ? selected.display_name : '';

  const filtered = useMemo(() => {
    const q = queryText.trim();
    const list = q === '' ? institutions : institutions.filter((i) => matchesQuery(i.display_name, q));
    // Preserve the incoming order but group by category for labelled sections.
    const groups: { category: string; items: InstitutionRow[] }[] = [];
    for (const inst of list) {
      let g = groups.find((x) => x.category === inst.category);
      if (!g) {
        g = { category: inst.category, items: [] };
        groups.push(g);
      }
      g.items.push(inst);
    }
    return groups;
  }, [institutions, queryText]);

  // Flat list of the currently rendered options, for keyboard navigation.
  const flat = useMemo(() => filtered.flatMap((g) => g.items), [filtered]);

  const commit = (inst: InstitutionRow) => {
    onChange(inst.institution_key);
    setQueryText('');
    setOpen(false);
  };

  return (
    <div
      ref={containerRef}
      className="relative min-w-[14rem]"
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setOpen(false);
      }}
    >
      <div className="relative">
        <Search className="w-3.5 h-3.5 text-slate-500 absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none" />
        <input
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-controls={`${labelId}-listbox`}
          aria-label="Search canonical institution"
          disabled={disabled}
          value={inputValue}
          placeholder={selected ? selected.display_name : 'search institution...'}
          onFocus={(e) => {
            setOpen(true);
            setQueryText('');
            setHighlight(0);
            e.currentTarget.select();
          }}
          onChange={(e) => {
            setQueryText(e.target.value);
            setOpen(true);
            setHighlight(0);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              setOpen(false);
              (e.target as HTMLInputElement).blur();
            } else if (e.key === 'ArrowDown') {
              e.preventDefault();
              setOpen(true);
              setHighlight((h) => Math.min(h + 1, Math.max(flat.length - 1, 0)));
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              setHighlight((h) => Math.max(h - 1, 0));
            } else if (e.key === 'Enter') {
              e.preventDefault();
              const pick = flat[highlight];
              if (pick) commit(pick);
            }
          }}
          className="w-full bg-slate-900 border border-slate-600 rounded pl-7 pr-2 py-1 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500"
        />
      </div>
      {open && (
        <div
          id={`${labelId}-listbox`}
          role="listbox"
          className="absolute z-20 mt-1 w-full max-h-56 overflow-y-auto rounded-lg border border-slate-600 bg-slate-900 shadow-xl"
        >
          {flat.length === 0 ? (
            <div className="px-3 py-2 text-xs text-slate-500">No institution matches "{queryText}"</div>
          ) : (
            filtered.map((g) => (
              <div key={g.category}>
                <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wide text-slate-500 sticky top-0 bg-slate-900">
                  {g.category}
                </div>
                {g.items.map((inst) => {
                  const idx = flat.indexOf(inst);
                  const isSel = inst.institution_key === value;
                  return (
                    <button
                      type="button"
                      key={inst.institution_key}
                      role="option"
                      aria-selected={isSel}
                      onMouseEnter={() => setHighlight(idx)}
                      onClick={() => commit(inst)}
                      className={
                        'w-full text-left px-3 py-1.5 text-xs flex items-center justify-between ' +
                        (idx === highlight ? 'bg-slate-700 text-white' : 'text-slate-300 hover:bg-slate-800')
                      }
                    >
                      <span>{inst.display_name}</span>
                      {isSel && <Check className="w-3.5 h-3.5 text-emerald-400" />}
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Dependency-free multi-select for linking an adviser to one or more brokers.
 * Replaces the native scroll-only <select multiple> (23+ entries): a type-ahead
 * filter narrows the option list by case-insensitive substring (type a surname),
 * options toggle on click, and the current selection renders as removable chips.
 * Escape clears the filter. onChange yields the selected broker_display names,
 * so the existing save flow is unchanged.
 */
const BrokerMultiSelect: React.FC<{
  candidates: BrokerCandidate[];
  value: string[];
  onChange: (names: string[]) => void;
  labelId: string;
}> = ({ candidates, value, onChange, labelId }) => {
  const [filter, setFilter] = useState('');

  const filtered = useMemo(() => {
    const q = filter.trim();
    return q === '' ? candidates : candidates.filter((b) => matchesQuery(b.broker_display, q));
  }, [candidates, filter]);

  const toggle = (name: string) => {
    onChange(value.includes(name) ? value.filter((n) => n !== name) : [...value, name]);
  };

  return (
    <div className="min-w-[14rem]">
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-1">
          {value.map((name) => (
            <span
              key={name}
              className="inline-flex items-center gap-1 bg-emerald-600/20 text-emerald-200 border border-emerald-600/40 rounded px-1.5 py-0.5 text-[11px]"
            >
              {name}
              <button
                type="button"
                aria-label={`Remove ${name}`}
                onClick={() => toggle(name)}
                className="hover:text-white"
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}
      <div className="relative">
        <Search className="w-3.5 h-3.5 text-slate-500 absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none" />
        <input
          type="text"
          aria-label="Filter brokers by name"
          aria-controls={`${labelId}-options`}
          value={filter}
          placeholder="type a surname to filter..."
          onChange={(e) => setFilter(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') setFilter('');
          }}
          className="w-full bg-slate-900 border border-slate-600 rounded pl-7 pr-2 py-1 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500"
        />
      </div>
      <div
        id={`${labelId}-options`}
        role="listbox"
        aria-multiselectable="true"
        className="mt-1 max-h-24 overflow-y-auto rounded border border-slate-700 bg-slate-900"
      >
        {filtered.length === 0 ? (
          <div className="px-2 py-1.5 text-[11px] text-slate-500">No broker matches "{filter}"</div>
        ) : (
          filtered.map((b) => {
            const isSel = value.includes(b.broker_display);
            return (
              <button
                type="button"
                key={b.broker_id}
                role="option"
                aria-selected={isSel}
                onClick={() => toggle(b.broker_display)}
                className={
                  'w-full text-left px-2 py-1 text-[11px] flex items-center justify-between ' +
                  (isSel ? 'bg-emerald-600/20 text-emerald-100' : 'text-slate-300 hover:bg-slate-800')
                }
              >
                <span>
                  {b.broker_display} <span className="text-slate-500">({b.loans})</span>
                </span>
                {isSel && <Check className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" />}
              </button>
            );
          })
        )}
      </div>
    </div>
  );
};

const inputClass =
  'w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500';
const labelClass = 'text-sm text-slate-400 block mb-1';

const CrmProvisioningPage: React.FC = () => {
  const [companies, setCompanies] = useState<CompanyOption[]>([]);
  const [companiesLoading, setCompaniesLoading] = useState(true);

  const [companyId, setCompanyId] = useState('');
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [authUrl, setAuthUrl] = useState('');
  const [adviserContactIds, setAdviserContactIds] = useState('');
  const [scope, setScope] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<ConnectResult | null>(null);
  // The companyId the last successful connect ran against — captured so a
  // subsequent "Trigger sync" targets the connected tenant even if the founder
  // changes the selector afterwards (result.connectionId belongs to THIS id).
  const [connectedCompanyId, setConnectedCompanyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<SyncResult | null>(null);
  const [syncError, setSyncError] = useState<string | null>(null);

  // ─── CRM mappings (white-glove) ───────────────────────────────────────────
  const [mappings, setMappings] = useState<MappingsData | null>(null);
  // The tenant the loaded mappings belong to, so saves always target the same
  // tenant even if the founder changes the selector afterwards.
  const [mappingsCompanyId, setMappingsCompanyId] = useState<string | null>(null);
  const [mappingsLoading, setMappingsLoading] = useState(false);
  const [mappingsError, setMappingsError] = useState<string | null>(null);
  // Per-row in-flight guard, keyed by a stable row id (adviser_id or lender key).
  const [savingRow, setSavingRow] = useState<string | null>(null);
  // Local edit buffers for adviser role + lender institution selections.
  const [lenderChoice, setLenderChoice] = useState<Record<string, string>>({});
  // Selected broker display names per adviser (multi-select buffer).
  const [brokerLinkChoice, setBrokerLinkChoice] = useState<Record<string, string[]>>({});
  // Mapped-lender corrections: replacement institution per row, the row armed
  // for removal (two-step confirm), and a text filter over the list.
  const [remapChoice, setRemapChoice] = useState<Record<string, string>>({});
  const [confirmRemove, setConfirmRemove] = useState<string | null>(null);
  const [mappedFilter, setMappedFilter] = useState('');

  // Load the company list for the selector. Founder can still type a raw
  // companyId if a tenant is not in the first page of results.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDocs(query(collection(db, 'companies'), limit(200)));
        if (cancelled) return;
        const list = snap.docs
          .map((d) => ({ id: d.id, companyName: (d.data() as any)?.companyName as string | undefined }))
          .sort((a, b) => (a.companyName || a.id).localeCompare(b.companyName || b.id));
        setCompanies(list);
      } catch (err) {
        // Non-fatal: the raw companyId text field still works.
        console.error('[CrmProvisioningPage] Failed to load companies', err);
      } finally {
        if (!cancelled) setCompaniesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setResult(null);
    setSyncResult(null);
    setSyncError(null);

    const trimmedCompanyId = companyId.trim();
    if (!trimmedCompanyId || !clientId.trim() || !clientSecret.trim() || !authUrl.trim()) {
      setError('companyId, clientId, clientSecret and authUrl are all required.');
      return;
    }
    if (!authUrl.trim().startsWith('https://')) {
      setError('authUrl must be an HTTPS URL (per-tenant Okta token endpoint).');
      return;
    }

    const adviserIds = adviserContactIds
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);

    setSubmitting(true);
    try {
      const callable = httpsCallable<
        {
          companyId: string;
          clientId: string;
          clientSecret: string;
          authUrl: string;
          adviserContactIds?: string[];
          scope?: string;
        },
        ConnectResult
      >(functions, 'adminConnectTenantCrm');

      const resp = await callable({
        companyId: trimmedCompanyId,
        clientId: clientId.trim(),
        clientSecret: clientSecret.trim(),
        authUrl: authUrl.trim(),
        ...(adviserIds.length ? { adviserContactIds: adviserIds } : {}),
        ...(scope.trim() ? { scope: scope.trim() } : {}),
      });

      setResult(resp.data);
      setConnectedCompanyId(trimmedCompanyId);
      // Clear the secret from the form after a successful connect.
      setClientSecret('');
    } catch (err: any) {
      setError(err?.message || 'MyCRM provisioning failed.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleTriggerSync = async () => {
    // Prefer the tenant that was actually connected this session (so the
    // connectionId and companyId belong together), else fall back to the
    // selected/entered tenant - this is what lets the standing button sync an
    // already-connected tenant with no connect performed this session.
    const targetCompanyId = connectedCompanyId ?? companyId.trim();
    if (!targetCompanyId) {
      setSyncError('Select or enter a tenant before triggering a sync.');
      return;
    }
    setSyncError(null);
    setSyncResult(null);
    setSyncing(true);
    try {
      const callable = httpsCallable<
        { companyId: string; connectionId?: string },
        SyncResult
      >(functions, 'adminTriggerTenantCrmSync');

      const resp = await callable({
        companyId: targetCompanyId,
        ...(result?.connectionId ? { connectionId: result.connectionId } : {}),
      });
      setSyncResult(resp.data);
    } catch (err: any) {
      setSyncError(err?.message || 'MyCRM sync failed.');
    } finally {
      setSyncing(false);
    }
  };

  // Forward a single mapping action through the founder-only admin callable,
  // which relays verbatim to the tenant `crmMappingAction`.
  async function callMappingAction<TRes = unknown>(payload: Record<string, unknown>): Promise<TRes> {
    const callable = httpsCallable<Record<string, unknown>, TRes>(functions, 'adminCrmMappingAction');
    const resp = await callable(payload);
    return resp.data;
  }

  const handleLoadMappings = async () => {
    const targetCompanyId = companyId.trim();
    if (!targetCompanyId) {
      setMappingsError('Select or type a tenant companyId before loading mappings.');
      return;
    }
    setMappingsError(null);
    setMappingsLoading(true);
    try {
      const data = await callMappingAction<MappingsData>({
        action: 'getMappingsData',
        companyId: targetCompanyId,
      });
      setMappings(data);
      setMappingsCompanyId(targetCompanyId);
      // Seed the multi-select buffers from the current mapping state.
      const seed: Record<string, string[]> = {};
      data.advisers.forEach((a) => {
        seed[a.adviser_id] = [];
      });
      setBrokerLinkChoice(seed);
      setLenderChoice({});
    } catch (err: any) {
      setMappings(null);
      setMappingsCompanyId(null);
      setMappingsError(err?.message || 'Failed to load CRM mappings.');
    } finally {
      setMappingsLoading(false);
    }
  };

  const handleSaveAdviserRole = async (adviserId: string, role: AdviserRole | null) => {
    if (!mappingsCompanyId) return;
    setSavingRow(`role:${adviserId}`);
    setMappingsError(null);
    try {
      await callMappingAction({
        action: 'saveAdviserRole',
        companyId: mappingsCompanyId,
        adviser_id: adviserId,
        role,
      });
      await handleLoadMappings();
    } catch (err: any) {
      setMappingsError(err?.message || 'Failed to save adviser classification.');
    } finally {
      setSavingRow(null);
    }
  };

  const handleSaveBrokerLink = async (adviserId: string) => {
    if (!mappingsCompanyId) return;
    const names = brokerLinkChoice[adviserId] ?? [];
    setSavingRow(`broker:${adviserId}`);
    setMappingsError(null);
    try {
      await callMappingAction({
        action: 'saveAdviserBrokerIds',
        companyId: mappingsCompanyId,
        adviser_id: adviserId,
        broker_display_names: names,
      });
      await handleLoadMappings();
    } catch (err: any) {
      setMappingsError(err?.message || 'Failed to link adviser to broker(s).');
    } finally {
      setSavingRow(null);
    }
  };

  const handleSaveLenderAlias = async (
    source: 'mycrm' | 'raw_commissions',
    lenderString: string
  ) => {
    if (!mappingsCompanyId) return;
    const key = `${source}:${lenderString}`;
    const institutionKey = lenderChoice[key];
    if (!institutionKey) {
      setMappingsError(`Choose a canonical institution for "${lenderString}" first.`);
      return;
    }
    setSavingRow(`lender:${key}`);
    setMappingsError(null);
    try {
      await callMappingAction({
        action: 'saveLenderAlias',
        companyId: mappingsCompanyId,
        source,
        lender_string: lenderString,
        institution_key: institutionKey,
      });
      await handleLoadMappings();
    } catch (err: any) {
      setMappingsError(err?.message || 'Failed to save lender mapping.');
    } finally {
      setSavingRow(null);
    }
  };

  const handleRemapLender = async (m: MappedLender) => {
    if (!mappingsCompanyId) return;
    const key = `${m.source}:${m.lender_string}`;
    const institutionKey = remapChoice[key];
    if (!institutionKey || institutionKey === m.institution_key) return;
    setSavingRow(`mapped:${key}`);
    setMappingsError(null);
    try {
      await callMappingAction({
        action: 'saveLenderAlias',
        companyId: mappingsCompanyId,
        source: m.source,
        lender_string: m.lender_string,
        institution_key: institutionKey,
      });
      setRemapChoice((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      await handleLoadMappings();
    } catch (err: any) {
      setMappingsError(err?.message || 'Failed to change lender mapping.');
    } finally {
      setSavingRow(null);
    }
  };

  const handleRemoveLender = async (m: MappedLender) => {
    if (!mappingsCompanyId) return;
    const key = `${m.source}:${m.lender_string}`;
    setSavingRow(`mapped:${key}`);
    setMappingsError(null);
    try {
      await callMappingAction({
        action: 'deleteLenderAlias',
        companyId: mappingsCompanyId,
        source: m.source,
        lender_string: m.lender_string,
      });
      setConfirmRemove(null);
      await handleLoadMappings();
    } catch (err: any) {
      setMappingsError(err?.message || 'Failed to remove lender mapping.');
    } finally {
      setSavingRow(null);
    }
  };

  const visibleMappedLenders = (mappings?.mappedLenders ?? []).filter(
    (m) =>
      mappedFilter.trim() === '' ||
      matchesQuery(`${m.lender_string} ${m.institution_name ?? ''} ${m.institution_key}`, mappedFilter)
  );

  return (
    <div className="min-h-screen bg-slate-900 p-6">
      {/* Header */}
      <div className="relative overflow-hidden rounded-2xl mb-6">
        <div className="absolute inset-0 bg-gradient-to-br from-teal-600 via-emerald-600 to-cyan-600 opacity-90"></div>
        <div className="relative px-8 py-12">
          <div className="flex items-center gap-3 mb-3">
            <Plug className="w-10 h-10 text-white" />
            <h1 className="text-4xl font-bold text-white">CRM Provisioning</h1>
          </div>
          <p className="text-white opacity-80 text-lg">
            Connect a tenant's MyCRM (Loan Market) API credentials without signing into their app.
            The tenant must already hold the CRM integration entitlement (pro tier). Leave adviser IDs
            blank to auto-discover the tenant's advisers, then trigger the first sync below.
          </p>
        </div>
      </div>

      <div className="max-w-3xl">
        <form
          onSubmit={handleSubmit}
          className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700/50 p-6 space-y-4"
        >
          <div>
            <label className={labelClass}>Tenant (company) *</label>
            <select
              value={companies.some((c) => c.id === companyId) ? companyId : ''}
              onChange={(e) => setCompanyId(e.target.value)}
              className={inputClass}
              disabled={companiesLoading}
            >
              <option value="">
                {companiesLoading ? 'Loading companies...' : 'Select a tenant (or type an ID below)'}
              </option>
              {companies.map((c) => (
                <option key={c.id} value={c.id}>
                  {(c.companyName || c.id) + ' (' + c.id + ')'}
                </option>
              ))}
            </select>
            <input
              type="text"
              value={companyId}
              onChange={(e) => setCompanyId(e.target.value)}
              placeholder="companyId (e.g. entourage)"
              className={inputClass + ' mt-2'}
              required
            />
          </div>

          <div>
            <label className={labelClass}>Client ID *</label>
            <input
              type="text"
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              placeholder="MyCRM OAuth client_id"
              className={inputClass}
              autoComplete="off"
              required
            />
          </div>

          <div>
            <label className={labelClass}>Client Secret *</label>
            <input
              type="password"
              value={clientSecret}
              onChange={(e) => setClientSecret(e.target.value)}
              placeholder="MyCRM OAuth client_secret"
              className={inputClass}
              autoComplete="new-password"
              required
            />
            <p className="text-xs text-slate-500 mt-1">
              Sent over HTTPS to the client function, which stores it in Secret Manager. It is never
              written to Firestore or logged.
            </p>
          </div>

          <div>
            <label className={labelClass}>Auth URL (per-tenant Okta token endpoint) *</label>
            <input
              type="text"
              value={authUrl}
              onChange={(e) => setAuthUrl(e.target.value)}
              placeholder="https://auth.mycrm.finance/oauth2/{authServerId}/v1/token"
              className={inputClass}
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Adviser Contact IDs (optional)</label>
              <input
                type="text"
                value={adviserContactIds}
                onChange={(e) => setAdviserContactIds(e.target.value)}
                placeholder="leave blank to auto-discover"
                className={inputClass}
              />
              <p className="text-xs text-slate-500 mt-1">
                Leave blank and connect will discover every adviser on the tenant from MyCRM and list
                them below. Only enter IDs to scope the sync to specific advisers.
              </p>
            </div>
            <div>
              <label className={labelClass}>OAuth Scopes (optional)</label>
              <input
                type="text"
                value={scope}
                onChange={(e) => setScope(e.target.value)}
                placeholder="default: api"
                className={inputClass}
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-lg px-5 py-2.5 transition-colors"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Connecting...
              </>
            ) : (
              <>
                <Plug className="w-4 h-4" /> Connect MyCRM
              </>
            )}
          </button>
        </form>

        {result && (
          <div className="mt-4 bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-5">
            <div className="flex items-center gap-2 text-emerald-400 font-semibold mb-2">
              <CheckCircle2 className="w-5 h-5" />
              {result.reusedExisting ? 'Connection updated' : 'Connection created'}
            </div>
            <p className="text-slate-300 text-sm">{result.message}</p>
            <p className="text-slate-400 text-xs mt-2">
              Connection ID: <span className="font-mono text-slate-300">{result.connectionId}</span>
            </p>

            {result.warning && (
              <div className="mt-3 flex items-start gap-2 text-amber-300 text-xs bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
                <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>{result.warning}</span>
              </div>
            )}

            {result.discoveredAdvisers && result.discoveredAdvisers.length > 0 && (
              <div className="mt-4">
                <div className="flex items-center gap-2 text-slate-300 text-sm font-semibold mb-2">
                  <Users className="w-4 h-4" /> Discovered advisers ({result.discoveredAdvisers.length})
                </div>
                <div className="rounded-lg border border-slate-700/60 overflow-hidden">
                  <table className="w-full text-left text-xs">
                    <thead className="bg-slate-800/60 text-slate-400">
                      <tr>
                        <th className="px-3 py-2 font-medium">Name</th>
                        <th className="px-3 py-2 font-medium">Email</th>
                        <th className="px-3 py-2 font-medium">Adviser ID</th>
                      </tr>
                    </thead>
                    <tbody className="text-slate-300">
                      {result.discoveredAdvisers.map((a) => (
                        <tr key={a.id} className="border-t border-slate-700/40">
                          <td className="px-3 py-2">{a.name}</td>
                          <td className="px-3 py-2">{a.email || '—'}</td>
                          <td className="px-3 py-2 font-mono">{a.id}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="text-slate-500 text-xs mt-2">
                  All {result.discoveredAdvisers.length} advisers were stored on the connection. Sync
                  will scope deals to each of them.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Standing sync trigger. Works for an ALREADY-connected tenant (page
            freshly loaded, no connect this session) as well as immediately after
            a connect: handleTriggerSync falls back to the selected companyId and
            omits connectionId (syncing all active connections) when no connect
            result is present, or reuses result.connectionId when it is. Enabled
            whenever a tenant is selected/entered. */}
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={handleTriggerSync}
            disabled={syncing || !companyId.trim()}
            className="inline-flex items-center gap-2 bg-cyan-600 hover:bg-cyan-500 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-lg px-4 py-2 text-sm transition-colors"
          >
            {syncing ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Syncing...
              </>
            ) : (
              <>
                <RefreshCw className="w-4 h-4" /> Trigger sync
              </>
            )}
          </button>
          <span className="text-xs text-slate-500">
            Syncs the selected tenant's active MyCRM connection(s). No connect needed if the tenant is
            already provisioned.
          </span>
        </div>

        {syncResult && (
          <div className="mt-4 bg-cyan-500/10 border border-cyan-500/30 rounded-xl p-5">
            <div className="flex items-center gap-2 text-cyan-300 font-semibold mb-3">
              <RefreshCw className="w-5 h-5" />
              Sync {syncResult.success ? 'complete' : 'completed with failures'}
            </div>
            {syncResult.outcomes.length === 0 ? (
              <p className="text-slate-300 text-sm">
                No active CRM connections were found for this tenant to sync.
              </p>
            ) : (
              <div className="space-y-3">
                {syncResult.outcomes.map((o) => (
                  <div
                    key={o.connectionId}
                    className="rounded-lg border border-slate-700/60 bg-slate-900/40 p-3 text-sm"
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-xs text-slate-400">{o.connectionId}</span>
                      <span
                        className={
                          'text-xs font-semibold px-2 py-0.5 rounded ' +
                          (o.status === 'success'
                            ? 'bg-emerald-500/20 text-emerald-300'
                            : o.status === 'partial'
                              ? 'bg-amber-500/20 text-amber-300'
                              : 'bg-red-500/20 text-red-300')
                        }
                      >
                        {o.status}
                      </span>
                    </div>
                    <div className="mt-2 grid grid-cols-3 gap-2 text-slate-300 text-xs">
                      <span>Advisers: {o.advisersSynced}</span>
                      <span>Deals: {o.dealsSynced}</span>
                      <span>Alerts: {o.alertsCreated}</span>
                    </div>
                    {o.errors.length > 0 && (
                      <ul className="mt-2 list-disc list-inside text-red-300 text-xs space-y-0.5">
                        {o.errors.map((msg, i) => (
                          <li key={i}>{msg}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {syncError && (
          <div className="mt-4 bg-red-500/10 border border-red-500/30 rounded-xl p-5">
            <div className="flex items-center gap-2 text-red-400 font-semibold mb-2">
              <AlertTriangle className="w-5 h-5" /> Sync failed
            </div>
            <p className="text-slate-300 text-sm whitespace-pre-wrap">{syncError}</p>
          </div>
        )}

        {error && (
          <div className="mt-4 bg-red-500/10 border border-red-500/30 rounded-xl p-5">
            <div className="flex items-center gap-2 text-red-400 font-semibold mb-2">
              <AlertTriangle className="w-5 h-5" /> Provisioning failed
            </div>
            <p className="text-slate-300 text-sm whitespace-pre-wrap">{error}</p>
          </div>
        )}
      </div>

      {/* ─── CRM Mappings (white-glove) ─────────────────────────────────── */}
      <div className="max-w-5xl mt-8">
        <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700/50 p-6">
          <div className="flex items-center justify-between gap-4 mb-2">
            <div className="flex items-center gap-2 text-white font-semibold text-lg">
              <ListChecks className="w-5 h-5 text-emerald-400" /> CRM Mappings
            </div>
            <button
              type="button"
              onClick={handleLoadMappings}
              disabled={mappingsLoading}
              className="inline-flex items-center gap-2 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-lg px-4 py-2 text-sm transition-colors"
            >
              {mappingsLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Loading...
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4" /> Load mappings
                </>
              )}
            </button>
          </div>
          <p className="text-slate-400 text-sm mb-4">
            Seed the selected tenant's adviser classification, adviser-to-broker links and lender
            name mappings. These feed the Trail Leakage detector, so a wrong mapping mutes or
            false-fires alerts. The tenant must hold the CRM entitlement (trail_leakage or
            broker_activity) or loading fails upstream.
          </p>

          {mappingsError && (
            <div className="mb-4 bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-sm text-red-300 whitespace-pre-wrap">
              {mappingsError}
            </div>
          )}

          {mappings && mappingsCompanyId && (
            <div className="space-y-6">
              <div className="text-xs text-slate-500">
                Loaded for tenant <span className="font-mono text-slate-300">{mappingsCompanyId}</span>
              </div>

              {/* Advisers: classification + broker link */}
              <div>
                <div className="flex items-center gap-2 text-slate-300 text-sm font-semibold mb-2">
                  <Users className="w-4 h-4" /> Advisers ({mappings.advisers.length})
                </div>
                {mappings.advisers.length === 0 ? (
                  <p className="text-slate-500 text-xs">
                    No advisers synced yet. Trigger a CRM sync above, then reload.
                  </p>
                ) : (
                  <div className="rounded-lg border border-slate-700/60 overflow-x-auto">
                    <table className="w-full text-left text-xs">
                      <thead className="bg-slate-800/60 text-slate-400">
                        <tr>
                          <th className="px-3 py-2 font-medium">Adviser</th>
                          <th className="px-3 py-2 font-medium">Deals</th>
                          <th className="px-3 py-2 font-medium">Classification</th>
                          <th className="px-3 py-2 font-medium">Mapped brokers</th>
                          <th className="px-3 py-2 font-medium">Link broker(s)</th>
                        </tr>
                      </thead>
                      <tbody className="text-slate-300">
                        {mappings.advisers.map((a) => (
                          <tr key={a.adviser_id} className="border-t border-slate-700/40 align-top">
                            <td className="px-3 py-2">
                              <div className="text-slate-200">{a.adviser_name || '(no name)'}</div>
                              <div className="text-slate-500">{a.email || 'n/a'}</div>
                              <div className="font-mono text-slate-600">{a.adviser_id}</div>
                            </td>
                            <td className="px-3 py-2">{a.deal_count}</td>
                            <td className="px-3 py-2">
                              <select
                                value={a.adviser_role ?? ''}
                                onChange={(e) =>
                                  handleSaveAdviserRole(
                                    a.adviser_id,
                                    e.target.value === '' ? null : (e.target.value as AdviserRole)
                                  )
                                }
                                disabled={savingRow === `role:${a.adviser_id}`}
                                className="bg-slate-900 border border-slate-600 rounded px-2 py-1 text-white"
                              >
                                <option value="">unclassified</option>
                                {ADVISER_ROLE_OPTIONS.map((r) => (
                                  <option key={r} value={r}>
                                    {r}
                                  </option>
                                ))}
                              </select>
                            </td>
                            <td className="px-3 py-2">
                              {a.valutten_broker_ids.length === 0 ? (
                                <span className="text-slate-500">none</span>
                              ) : (
                                <span className="font-mono text-slate-400">
                                  {a.valutten_broker_ids.length} id(s)
                                </span>
                              )}
                            </td>
                            <td className="px-3 py-2">
                              <div className="flex items-start gap-2">
                                <BrokerMultiSelect
                                  candidates={mappings.brokerCandidates}
                                  value={brokerLinkChoice[a.adviser_id] ?? []}
                                  onChange={(names) =>
                                    setBrokerLinkChoice((prev) => ({
                                      ...prev,
                                      [a.adviser_id]: names,
                                    }))
                                  }
                                  labelId={`broker-${a.adviser_id}`}
                                />
                                <button
                                  type="button"
                                  onClick={() => handleSaveBrokerLink(a.adviser_id)}
                                  disabled={savingRow === `broker:${a.adviser_id}`}
                                  className="inline-flex items-center gap-1 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white rounded px-2 py-1 flex-shrink-0"
                                >
                                  {savingRow === `broker:${a.adviser_id}` ? (
                                    <Loader2 className="w-3 h-3 animate-spin" />
                                  ) : (
                                    <Save className="w-3 h-3" />
                                  )}
                                  Save
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Unmapped lenders */}
              <div>
                <div className="flex items-center gap-2 text-slate-300 text-sm font-semibold mb-2">
                  <Plug className="w-4 h-4" /> Unmapped lender names
                </div>
                <p className="flex items-start gap-1.5 text-amber-300/90 text-xs mb-2">
                  <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  Lender mappings are shared by every tenant. Saving, changing or removing one here
                  changes trail leakage and Client Care matching for all of them. Leave non-mortgage
                  lenders (asset finance, business, personal) unmapped.
                </p>
                {mappings.unmappedLenders.mycrm.length === 0 &&
                mappings.unmappedLenders.raw_commissions.length === 0 ? (
                  <p className="text-slate-500 text-xs">
                    Every observed lender name is already mapped to a canonical institution.
                  </p>
                ) : (
                  <div className="rounded-lg border border-slate-700/60 overflow-x-auto">
                    <table className="w-full text-left text-xs">
                      <thead className="bg-slate-800/60 text-slate-400">
                        <tr>
                          <th className="px-3 py-2 font-medium">Source</th>
                          <th className="px-3 py-2 font-medium">Lender string</th>
                          <th className="px-3 py-2 font-medium">Seen</th>
                          <th className="px-3 py-2 font-medium">Canonical institution</th>
                          <th className="px-3 py-2 font-medium"></th>
                        </tr>
                      </thead>
                      <tbody className="text-slate-300">
                        {(
                          [
                            ...mappings.unmappedLenders.mycrm.map((l) => ({ source: 'mycrm' as const, l })),
                            ...mappings.unmappedLenders.raw_commissions.map((l) => ({
                              source: 'raw_commissions' as const,
                              l,
                            })),
                          ]
                        ).map(({ source, l }) => {
                          const key = `${source}:${l.lender_string}`;
                          return (
                            <tr key={key} className="border-t border-slate-700/40">
                              <td className="px-3 py-2 font-mono text-slate-500">{source}</td>
                              <td className="px-3 py-2">{l.lender_string}</td>
                              <td className="px-3 py-2">{l.occurrences ?? l.loan_count ?? 0}</td>
                              <td className="px-3 py-2">
                                <InstitutionCombobox
                                  institutions={mappings.institutions}
                                  value={lenderChoice[key] ?? ''}
                                  onChange={(institutionKey) =>
                                    setLenderChoice((prev) => ({ ...prev, [key]: institutionKey }))
                                  }
                                  labelId={`lender-${key}`}
                                />
                              </td>
                              <td className="px-3 py-2">
                                <button
                                  type="button"
                                  onClick={() => handleSaveLenderAlias(source, l.lender_string)}
                                  disabled={savingRow === `lender:${key}`}
                                  className="inline-flex items-center gap-1 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white rounded px-2 py-1"
                                >
                                  {savingRow === `lender:${key}` ? (
                                    <Loader2 className="w-3 h-3 animate-spin" />
                                  ) : (
                                    <Save className="w-3 h-3" />
                                  )}
                                  Save
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Mapped lenders: correct or remove a saved mapping */}
              <div>
                <div className="flex items-center justify-between gap-3 mb-2">
                  <div className="flex items-center gap-2 text-slate-300 text-sm font-semibold">
                    <ListChecks className="w-4 h-4" /> Mapped lender names
                    <span className="text-slate-500 font-normal">
                      ({mappings.mappedLenders.length})
                    </span>
                  </div>
                  <div className="relative">
                    <Search className="w-3.5 h-3.5 text-slate-500 absolute left-2 top-1/2 -translate-y-1/2" />
                    <input
                      type="text"
                      value={mappedFilter}
                      onChange={(e) => setMappedFilter(e.target.value)}
                      placeholder="Filter lender or institution"
                      aria-label="Filter mapped lenders"
                      className="bg-slate-800 border border-slate-700 rounded pl-7 pr-2 py-1 text-xs text-slate-200 w-56"
                    />
                  </div>
                </div>
                {mappings.mappedLenders.length === 0 ? (
                  <p className="text-slate-500 text-xs">
                    None of this tenant's lender names are mapped yet.
                  </p>
                ) : (
                  <div className="rounded-lg border border-slate-700/60 overflow-x-auto">
                    <table className="w-full text-left text-xs">
                      <thead className="bg-slate-800/60 text-slate-400">
                        <tr>
                          <th className="px-3 py-2 font-medium">Source</th>
                          <th className="px-3 py-2 font-medium">Lender string</th>
                          <th className="px-3 py-2 font-medium">Seen</th>
                          <th className="px-3 py-2 font-medium">Mapped to</th>
                          <th className="px-3 py-2 font-medium">Change to</th>
                          <th className="px-3 py-2 font-medium"></th>
                        </tr>
                      </thead>
                      <tbody className="text-slate-300">
                        {visibleMappedLenders.map((m) => {
                          const key = `${m.source}:${m.lender_string}`;
                          const busy = savingRow === `mapped:${key}`;
                          const choice = remapChoice[key] ?? '';
                          const canRemap = choice !== '' && choice !== m.institution_key;
                          return (
                            <tr key={key} className="border-t border-slate-700/40">
                              <td className="px-3 py-2 font-mono text-slate-500">{m.source}</td>
                              <td className="px-3 py-2">{m.lender_string}</td>
                              <td className="px-3 py-2">{m.seen}</td>
                              <td className="px-3 py-2">
                                <div>{m.institution_name ?? m.institution_key}</div>
                                <div className="text-slate-500 font-mono">
                                  {m.institution_key}
                                  {m.confidence ? ` · ${m.confidence}` : ''}
                                </div>
                              </td>
                              <td className="px-3 py-2">
                                <InstitutionCombobox
                                  institutions={mappings.institutions}
                                  value={choice}
                                  onChange={(institutionKey) =>
                                    setRemapChoice((prev) => ({ ...prev, [key]: institutionKey }))
                                  }
                                  labelId={`mapped-${key}`}
                                  disabled={busy}
                                />
                              </td>
                              <td className="px-3 py-2">
                                <div className="flex items-center gap-1.5 whitespace-nowrap">
                                  <button
                                    type="button"
                                    onClick={() => handleRemapLender(m)}
                                    disabled={busy || !canRemap}
                                    className="inline-flex items-center gap-1 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 text-white rounded px-2 py-1"
                                  >
                                    {busy ? (
                                      <Loader2 className="w-3 h-3 animate-spin" />
                                    ) : (
                                      <Save className="w-3 h-3" />
                                    )}
                                    Save
                                  </button>
                                  {confirmRemove === key ? (
                                    <>
                                      <button
                                        type="button"
                                        onClick={() => handleRemoveLender(m)}
                                        disabled={busy}
                                        className="inline-flex items-center gap-1 bg-red-600 hover:bg-red-500 disabled:opacity-50 text-white rounded px-2 py-1"
                                      >
                                        <Trash2 className="w-3 h-3" />
                                        Remove for all tenants
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => setConfirmRemove(null)}
                                        disabled={busy}
                                        className="inline-flex items-center gap-1 text-slate-400 hover:text-slate-200 px-1 py-1"
                                      >
                                        <X className="w-3 h-3" />
                                        Cancel
                                      </button>
                                    </>
                                  ) : (
                                    <button
                                      type="button"
                                      onClick={() => setConfirmRemove(key)}
                                      disabled={busy}
                                      className="inline-flex items-center gap-1 border border-slate-600 hover:border-red-500 hover:text-red-300 text-slate-300 rounded px-2 py-1"
                                    >
                                      <Trash2 className="w-3 h-3" />
                                      Remove
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                        {visibleMappedLenders.length === 0 && (
                          <tr className="border-t border-slate-700/40">
                            <td colSpan={6} className="px-3 py-3 text-slate-500">
                              No mapped lender matches that filter.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CrmProvisioningPage;
